import { useCallback, useId, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Carousel, { useCarousel } from '_components/Carousel';
import LinkButton from '_components/UI/LinkButton';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackPlpCrossLinkingCtaClick } from '_utils/GA4/trackPlpCrossLinkingEvent';
import PlpCrossLinkingCard from './PlpCrossLinkingCard';
import type { PlpCrossLinkingCard as Card, PlpCrossLinkingData } from './types';
import styles from './PlpCrossLinking.module.scss';

export interface PlpCrossLinkingProps {
  data: PlpCrossLinkingData;
  locale?: string;
}

/** Images above this index load lazily; the first row is visible on arrival. */
const PRIORITY_CARD_COUNT = 3;

/**
 * Card gap widens from 4px to 20px at the desktop layout flip. Swiper only reads
 * `spaceBetween` from its own breakpoint map, so it cannot live in the module —
 * `988` is the px value of the `tablet-xl` breakpoint used in the SCSS.
 */
const DESKTOP_TRACK_BREAKPOINTS = { 988: { spaceBetween: 20 } };

const buildCtaHref = (raw: string | null, locale?: string): string => {
  const value = raw?.trim();

  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;

  const path = value.startsWith('/') ? value : `/${value}`;

  return `/${locale ?? 'en'}${path}`;
};

// ─── Inner component (has access to Carousel context) ─────────────────────────

interface PlpCrossLinkingInnerProps {
  heading: string;
  headingId: string;
  ctaLabel: string | null;
  ctaHref: string;
  cards: Card[];
  locale?: string;
  coOpBrand: string | null;
}

function PlpCrossLinkingInner({
  heading,
  headingId,
  ctaLabel,
  ctaHref,
  cards,
  locale,
  coOpBrand,
}: PlpCrossLinkingInnerProps) {
  const { t } = useTranslation('common');
  const { asPath } = useRouter();
  const {
    state: { isPrevVisible, isNextVisible },
  } = useCarousel();

  // Arrows only when the track can actually scroll. They are also hidden below
  // the desktop breakpoint in CSS, where the design uses native touch scrolling.
  const showArrows = isPrevVisible || isNextVisible;

  const handleCtaClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const data = {
        // Query string dropped so the CTA and the cards report the same page.
        interactionLocation: asPath.split('?')[0],
        linkUrl: ctaHref,
        linkText: ctaLabel ?? '',
        coOpBrand: coOpBrand || null,
      };

      // A modified click keeps this page alive, so the hit is not at risk and
      // navigation must not be hijacked.
      if (isModifiedClick(e)) {
        trackPlpCrossLinkingCtaClick({ data });
        return;
      }

      e.preventDefault();
      trackPlpCrossLinkingCtaClick({
        data,
        eventCallback: () => {
          window.location.href = ctaHref;
        },
      });
    },
    [asPath, ctaHref, ctaLabel, coOpBrand]
  );

  return (
    <>
      <div className={styles.rail}>
        <h2 className={styles.heading} id={headingId}>
          {heading}
        </h2>

        {ctaLabel && ctaHref && (
          <LinkButton
            href={ctaHref}
            variant="black"
            className={styles.cta}
            onClick={handleCtaClick}
          >
            {ctaLabel}
          </LinkButton>
        )}

        {showArrows && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('plpCrossLinking.navPrev')}
              className={styles.navButton}
            />
            <Carousel.NavNext
              aria-label={t('plpCrossLinking.navNext')}
              className={styles.navButton}
            />
          </div>
        )}
      </div>

      <Carousel.Track
        className={styles.swiper}
        spaceBetween={4}
        breakpoints={DESKTOP_TRACK_BREAKPOINTS}
      >
        {cards.map((card, index) => (
          <Carousel.Slide
            key={`${card.record.objectID}-${card.variantSku ?? ''}`}
            className={styles.slide}
          >
            <PlpCrossLinkingCard
              record={card.record}
              variantSku={card.variantSku}
              locale={locale}
              priority={index < PRIORITY_CARD_COUNT}
              index={index}
              coOpBrand={coOpBrand}
            />
          </Carousel.Slide>
        ))}
      </Carousel.Track>
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * Merch-curated cross-linking band rendered inside the PLP product grid (ED-211).
 *
 * Heading, optional CTA, and the product list all come from Prismic; the product
 * records are resolved server-side, so there is no loading state here — the band
 * either has cards and renders, or renders nothing at all.
 */
const PlpCrossLinking = ({ data, locale }: PlpCrossLinkingProps) => {
  const headingId = useId();

  if (!data?.heading || !data.cards?.length) return null;

  return (
    <section className={styles.root} aria-labelledby={headingId}>
      <div className={styles.container}>
        <Carousel.Provider>
          <PlpCrossLinkingInner
            heading={data.heading}
            headingId={headingId}
            ctaLabel={data.ctaLabel}
            ctaHref={buildCtaHref(data.ctaUrl, locale)}
            cards={data.cards}
            locale={locale}
            coOpBrand={data.coopBrand}
          />
        </Carousel.Provider>
      </div>
    </section>
  );
};

export default PlpCrossLinking;
